<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from 'firebase/storage'
import { doc, updateDoc } from 'firebase/firestore'
import { storage, db } from '@/firebase'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { writeAuditLog } from '@/services/audit'

const props = withDefaults(
  defineProps<{
    subjectId: string
    photoUrl: string | null
    size?: 'sm' | 'md' | 'lg'
  }>(),
  { size: 'md' },
)

const route = useRoute()
const incidentId = route.params.id as string

const syncStore = useSyncStore()
const toast = useToastStore()

const uploading = ref(false)
const progress = ref(0)
const imgError = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const MAX_SIZE = 10 * 1024 * 1024

function triggerPicker() {
  if (syncStore.isOnline === false) {
    toast.add('Upload requires network connection', 'warning', 3000)
    return
  }
  fileInput.value?.click()
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // Reset so same file can be selected again
  input.value = ''

  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    toast.add('Only JPEG and PNG images are accepted', 'error', 4000)
    return
  }

  if (file.size > MAX_SIZE) {
    toast.add('Maximum file size is 10 MB', 'error', 4000)
    return
  }

  const ext = file.type === 'image/png' ? 'png' : 'jpg'
  const path = `incidents/${incidentId}/subjects/${props.subjectId}/photo.${ext}`

  uploading.value = true
  progress.value = 0
  imgError.value = false

  try {
    const fileRef = storageRef(storage, path)
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type })

    await new Promise<void>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          progress.value = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        },
        reject,
        resolve,
      )
    })

    const photoUrl = await getDownloadURL(uploadTask.snapshot.ref)

    await updateDoc(doc(db, 'incidents', incidentId, 'subjects', props.subjectId), {
      photo_url: photoUrl,
    })

    await writeAuditLog(incidentId, 'subject.photo_updated', 'subject', props.subjectId, null, {
      photo_url: photoUrl,
    })

    // onSnapshot listener in subjectsStore will reactively update the UI
    toast.add('Photo uploaded', 'success', 2000)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Upload failed'
    toast.add(msg, 'error', 4000)
  } finally {
    uploading.value = false
    progress.value = 0
  }
}
</script>

<template>
  <div
    class="relative group overflow-hidden bg-surface-container-high shrink-0 rounded"
    :class="size === 'sm' ? 'w-20 h-20' : size === 'lg' ? 'w-32 h-44' : 'w-32 h-32'"
  >
    <!-- Photo or placeholder -->
    <img
      v-if="photoUrl && !imgError"
      :src="photoUrl"
      alt="Subject photo"
      class="w-full h-full object-cover"
      @error="imgError = true"
    />
    <img
      v-else
      src="/assets/subject-placeholder.svg"
      alt="No photo"
      class="w-full h-full object-cover"
    />

    <!-- Upload progress overlay -->
    <div
      v-if="uploading"
      class="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2"
    >
      <div class="w-5 h-5 border-2 border-white/30 border-t-white animate-spin" />
      <span class="text-white text-xs font-medium">{{ progress }}%</span>
    </div>

    <!-- Hover overlay with action button -->
    <div
      v-else
      class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3"
    >
      <button
        type="button"
        class="text-white text-xs font-mono uppercase tracking-widest bg-black/50 hover:bg-black/70 px-3 py-1.5 transition-colors"
        @click="triggerPicker"
      >
        {{ photoUrl && !imgError ? 'Replace Photo' : 'Add Photo' }}
      </button>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>
